import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    collection, query, where, getDocs, limit,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import {
    ShieldCheck, Search, CheckCircle2, XCircle, Loader,
    Building2, MapPin, Mail, Hash, RefreshCw, Phone, AlertTriangle,
} from 'lucide-react';

/**
 * 가입 치과 클레임 관리
 *
 * 워크플로우:
 *   1. 치과 회원가입 → users 컬렉션 추가
 *   2. (자동 매칭 성공) → hospitals.claimedBy 설정 → 클레임 활성
 *      (자동 매칭 실패) → 클레임 비활성
 *   3. 관리자가 비활성 치과를 검색·확인 → hospitals 검색 → 할당
 *   4. 할당 즉시 → 클레임 활성 자동 전환
 */
function AdminClinicClaimManager() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all'); // all | active | inactive
    const [active, setActive] = useState(null);  // 할당 모달 대상
    const [busy, setBusy] = useState(null);      // 해제 중인 user.id

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            // 치과 사용자 전체 (businessType: clinic | dental | 치과)
            const types = ['clinic', 'dental', '치과'];
            const all = [];
            for (const t of types) {
                const snap = await getDocs(query(
                    collection(db, 'users'),
                    where('businessType', '==', t),
                    limit(500),
                ));
                snap.docs.forEach(d => all.push({ id: d.id, ...d.data() }));
            }
            // 중복 제거
            const seen = new Set();
            const dedup = all.filter(u => { if (seen.has(u.id)) return false; seen.add(u.id); return true; });

            // 각 사용자의 claim 상태 조회
            const enriched = await Promise.all(dedup.map(async (u) => {
                try {
                    const cs = await getDocs(query(
                        collection(db, 'hospitals'),
                        where('claimedBy', '==', u.id),
                        limit(1),
                    ));
                    const claim = cs.empty ? null : { id: cs.docs[0].id, ...cs.docs[0].data() };
                    return { ...u, claim };
                } catch {
                    return { ...u, claim: null };
                }
            }));
            setUsers(enriched);
        } catch (e) {
            setError(e.message);
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const release = async (u) => {
        if (!u.claim) return;
        const reason = window.prompt(
            `${u.businessName || u.email} 의 클레임 (${u.claim.name}) 을 해제할까요?\n사유를 입력하세요:`,
            '관리자 검수 결과 미일치',
        );
        if (!reason) return;
        setBusy(u.id);
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            await httpsCallable(fns, 'adminRejectClaim')({ hospitalId: u.claim.id, reason });
            await load();
        } catch (e) {
            alert('해제 실패: ' + e.message);
        }
        setBusy(null);
    };

    const filtered = useMemo(() => {
        return users.filter(u => {
            const isActive = !!u.claim;
            if (filter === 'active' && !isActive) return false;
            if (filter === 'inactive' && isActive) return false;
            const t = search.trim().toLowerCase();
            if (!t) return true;
            const hay = [
                u.businessName, u.representativeName, u.email,
                u.businessNumber, u.address, u.phone, u.claim?.name, u.claim?.address,
            ].filter(Boolean).join(' ').toLowerCase();
            return hay.includes(t);
        });
    }, [users, filter, search]);

    const counts = useMemo(() => ({
        all: users.length,
        active: users.filter(u => !!u.claim).length,
        inactive: users.filter(u => !u.claim).length,
    }), [users]);

    return (
        <div style={S.page}>
            <div style={S.header}>
                <h1 style={S.title}>
                    <ShieldCheck size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    가입 치과 클레임 관리
                </h1>
                <p style={S.subtitle}>
                    가입된 치과 회원 중 클레임 비활성 상태인 곳을 검색·할당합니다.
                    할당 즉시 클레임이 활성화되며 환자앱에서 예약을 받을 수 있습니다.
                </p>
            </div>

            {/* 검색 + 필터 (최상단) */}
            <div style={S.controlBar}>
                <div style={S.searchWrap}>
                    <Search size={14} color="#64748b" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="치과명 / 이메일 / 사업자번호 / 주소 검색"
                        style={S.searchInput}
                    />
                </div>
                <div style={S.filterGroup}>
                    <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
                        전체 {counts.all}
                    </FilterChip>
                    <FilterChip active={filter === 'inactive'} color="#ef4444" onClick={() => setFilter('inactive')}>
                        클레임 비활성 {counts.inactive}
                    </FilterChip>
                    <FilterChip active={filter === 'active'} color="#10b981" onClick={() => setFilter('active')}>
                        클레임 활성 {counts.active}
                    </FilterChip>
                </div>
                <button onClick={load} disabled={loading} style={S.refreshBtn}>
                    {loading ? <Loader size={12} /> : <RefreshCw size={12} />} 새로고침
                </button>
            </div>

            {error && (
                <div style={S.error}><AlertTriangle size={14} /> {error}</div>
            )}

            {/* 결과 */}
            {loading ? (
                <div style={S.empty}><Loader size={20} /> 로딩 중…</div>
            ) : filtered.length === 0 ? (
                <div style={S.empty}>
                    <CheckCircle2 size={20} color="#94a3b8" /> 결과 없음
                </div>
            ) : (
                <div style={S.tableWrap}>
                    <table style={S.table}>
                        <thead>
                            <tr style={S.tr}>
                                <th style={S.th}>치과명</th>
                                <th style={S.th}>주소</th>
                                <th style={S.th}>이메일</th>
                                <th style={S.th}>사업자번호</th>
                                <th style={S.th}>매칭된 치과</th>
                                <th style={S.thRight}>액션</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(u => {
                                const isActive = !!u.claim;
                                return (
                                    <tr key={u.id} style={S.row}>
                                        <td style={S.td}>
                                            <div style={S.nameCell}>
                                                <Building2 size={13} color="#64748b" />
                                                <strong>{u.businessName || u.representativeName || '(이름 없음)'}</strong>
                                                <ClaimBadge active={isActive} />
                                            </div>
                                            {u.representativeName && u.businessName && (
                                                <div style={S.subText}>대표 {u.representativeName}</div>
                                            )}
                                        </td>
                                        <td style={S.td}>
                                            <div style={S.cellInline}><MapPin size={11} /> {u.address || '-'}</div>
                                            {u.phone && <div style={S.subText}><Phone size={10} /> {u.phone}</div>}
                                        </td>
                                        <td style={S.td}>
                                            <div style={S.cellInline}><Mail size={11} /> {u.email || '-'}</div>
                                        </td>
                                        <td style={S.td}>
                                            <div style={S.cellInline}><Hash size={11} /> {u.businessNumber || '-'}</div>
                                        </td>
                                        <td style={S.td}>
                                            {isActive ? (
                                                <div style={{ fontSize: 12 }}>
                                                    <strong>{u.claim.name}</strong>
                                                    <div style={S.subText}>{u.claim.address}</div>
                                                    {u.claim.matchStatus && (
                                                        <span style={S.statusTag}>{u.claim.matchStatus}</span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span style={{ fontSize: 12, color: '#94a3b8' }}>—</span>
                                            )}
                                        </td>
                                        <td style={S.tdRight}>
                                            {isActive ? (
                                                <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                                                    <button
                                                        onClick={() => setActive(u)}
                                                        disabled={busy === u.id}
                                                        style={S.smallBtn}
                                                    >
                                                        재할당
                                                    </button>
                                                    <button
                                                        onClick={() => release(u)}
                                                        disabled={busy === u.id}
                                                        style={S.releaseBtn}
                                                    >
                                                        {busy === u.id ? <Loader size={12} /> : <XCircle size={12} />} 해제
                                                    </button>
                                                </div>
                                            ) : (
                                                <button onClick={() => setActive(u)} style={S.assignBtn}>
                                                    <Search size={12} /> 클레임 할당
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {active && (
                <AssignModal
                    user={active}
                    onClose={() => setActive(null)}
                    onDone={() => { setActive(null); load(); }}
                />
            )}
        </div>
    );
}

/* ============== 클레임 활성/비활성 배지 ============== */
function ClaimBadge({ active }) {
    return active ? (
        <span style={{
            padding: '2px 7px', background: '#d1fae5', color: '#065f46',
            borderRadius: 4, fontSize: 10, fontWeight: 800,
            display: 'inline-flex', alignItems: 'center', gap: 3,
        }}>
            <CheckCircle2 size={9} /> 클레임 활성
        </span>
    ) : (
        <span style={{
            padding: '2px 7px', background: '#fee2e2', color: '#991b1b',
            borderRadius: 4, fontSize: 10, fontWeight: 800,
            display: 'inline-flex', alignItems: 'center', gap: 3,
        }}>
            <XCircle size={9} /> 클레임 비활성
        </span>
    );
}

/* ============== 필터 칩 ============== */
function FilterChip({ active, color = '#0ea5e9', onClick, children }) {
    return (
        <button onClick={onClick} style={{
            padding: '6px 12px',
            background: active ? color : '#fff',
            color: active ? '#fff' : '#475569',
            border: '1px solid ' + (active ? color : '#cbd5e1'),
            borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
            whiteSpace: 'nowrap',
        }}>{children}</button>
    );
}

/* ============== 치과 검색·할당 모달 ============== */
function AssignModal({ user, onClose, onDone }) {
    const [search, setSearch] = useState(user.businessName || '');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [busy, setBusy] = useState(false);
    const fns = getFunctions(undefined, 'asia-northeast3');

    const doSearch = async () => {
        if (!search.trim()) return;
        setSearching(true);
        try {
            const text = search.trim();
            const snap = await getDocs(query(
                collection(db, 'hospitals'),
                where('name', '>=', text),
                where('name', '<=', text + ''),
                limit(50),
            ));
            setResults(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            alert('검색 실패: ' + e.message);
        }
        setSearching(false);
    };

    useEffect(() => { doSearch(); /* eslint-disable-next-line */ }, []);

    const assign = async (h) => {
        if (h.claimedBy && h.claimedBy !== user.id) {
            alert('이미 다른 사용자가 등록한 치과입니다');
            return;
        }
        if (!window.confirm(`${h.name} 을(를) ${user.email} 에게 할당할까요?\n할당 즉시 클레임이 활성화됩니다.`)) return;
        setBusy(true);
        try {
            await httpsCallable(fns, 'adminAssignClaim')({
                hospitalId: h.id,
                targetUid: user.id,
            });
            alert('클레임 활성화 완료');
            onDone();
        } catch (e) {
            alert(e.message);
        }
        setBusy(false);
    };

    return (
        <div style={S.overlay} onClick={onClose}>
            <div style={S.modal} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 800 }}>
                    클레임 할당 — <span style={{ color: '#0ea5e9' }}>{user.businessName}</span>
                </h3>
                <div style={S.modalUserBox}>
                    <div><strong>이메일:</strong> {user.email}</div>
                    <div><strong>사업자번호:</strong> {user.businessNumber || '-'}</div>
                    <div><strong>주소:</strong> {user.address || '-'}</div>
                    <div><strong>연락처:</strong> {user.phone || '-'}</div>
                    {user.claim && (
                        <div style={{ marginTop: 6, color: '#92400e' }}>
                            <strong>현재 매칭:</strong> {user.claim.name} ({user.claim.address})
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                        style={S.modalSearchInput}
                        placeholder="hospitals 컬렉션에서 치과명 검색"
                    />
                    <button onClick={doSearch} disabled={searching} style={S.modalSearchBtn}>
                        {searching ? <Loader size={12} /> : <Search size={12} />} 검색
                    </button>
                </div>

                <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                    {results.length === 0 ? (
                        <div style={S.empty}>검색 결과 없음</div>
                    ) : results.map(h => (
                        <div key={h.id} style={S.resultRow}>
                            <div style={{ flex: 1 }}>
                                <strong>{h.name}</strong>
                                {h.claimedBy && h.claimedBy !== user.id && (
                                    <span style={{
                                        marginLeft: 6, padding: '2px 6px',
                                        background: '#fef3c7', color: '#92400e',
                                        borderRadius: 3, fontSize: 9, fontWeight: 800,
                                    }}>이미 등록됨</span>
                                )}
                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                                    📍 {h.address || '-'} · ☎ {h.phone || '-'}
                                </div>
                            </div>
                            <button
                                onClick={() => assign(h)}
                                disabled={busy || (h.claimedBy && h.claimedBy !== user.id)}
                                style={S.modalAssignBtn}
                            >
                                할당
                            </button>
                        </div>
                    ))}
                </div>

                <div style={{ marginTop: 12, textAlign: 'right' }}>
                    <button onClick={onClose} style={S.modalCancelBtn}>닫기</button>
                </div>
            </div>
        </div>
    );
}

const S = {
    page: { maxWidth: 1280, margin: '0 auto', padding: 24 },
    header: { marginBottom: 16 },
    title: { fontSize: 22, fontWeight: 900, margin: 0, color: '#0f172a' },
    subtitle: { fontSize: 13, color: '#64748b', marginTop: 6, lineHeight: 1.6 },

    controlBar: {
        display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center',
        background: '#fff', padding: 12, border: '1px solid #e2e8f0', borderRadius: 10,
        flexWrap: 'wrap',
    },
    searchWrap: {
        flex: 1, minWidth: 280, display: 'flex', alignItems: 'center', gap: 6,
        padding: '8px 12px', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 6,
    },
    searchInput: {
        flex: 1, border: 'none', outline: 'none', background: 'transparent',
        fontSize: 13, color: '#0f172a',
    },
    filterGroup: { display: 'flex', gap: 6 },
    refreshBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '8px 12px', background: '#fff', border: '1px solid #cbd5e1',
        borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', color: '#64748b',
    },

    error: {
        padding: 12, marginBottom: 12, background: '#fee2e2', color: '#991b1b',
        borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
    },
    empty: {
        padding: 40, textAlign: 'center', background: '#fff',
        border: '1px solid #e2e8f0', borderRadius: 8,
        fontSize: 13, color: '#94a3b8',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    },

    tableWrap: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    tr: { background: '#f8fafc' },
    th: {
        padding: '10px 12px', textAlign: 'left',
        fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase',
        borderBottom: '1px solid #e2e8f0',
    },
    thRight: {
        padding: '10px 12px', textAlign: 'right',
        fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase',
        borderBottom: '1px solid #e2e8f0',
    },
    row: { borderBottom: '1px solid #f1f5f9' },
    td: { padding: '12px', fontSize: 13, color: '#0f172a', verticalAlign: 'top' },
    tdRight: { padding: '12px', fontSize: 13, color: '#0f172a', textAlign: 'right', verticalAlign: 'top' },

    nameCell: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
    cellInline: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 },
    subText: { fontSize: 11, color: '#94a3b8', marginTop: 3, display: 'flex', alignItems: 'center', gap: 3 },
    statusTag: {
        marginTop: 4, display: 'inline-block', padding: '1px 6px',
        background: '#e0f2fe', color: '#075985', borderRadius: 3,
        fontSize: 10, fontWeight: 700,
    },

    assignBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '7px 12px', background: '#0ea5e9', color: '#fff',
        border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    smallBtn: {
        padding: '6px 10px', background: '#fff', color: '#64748b',
        border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
    },
    releaseBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '6px 10px', background: '#fff', color: '#ef4444',
        border: '1px solid #ef4444', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
    },

    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    },
    modal: {
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 720, padding: 20,
        maxHeight: '90vh', overflowY: 'auto',
    },
    modalUserBox: {
        padding: 10, marginBottom: 14, background: '#f8fafc',
        border: '1px solid #e2e8f0', borderRadius: 6,
        fontSize: 12, color: '#475569', lineHeight: 1.7,
    },
    modalSearchInput: {
        flex: 1, padding: '8px 10px', border: '1px solid #cbd5e1',
        borderRadius: 6, fontSize: 13,
    },
    modalSearchBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '8px 12px', background: '#0ea5e9', color: '#fff',
        border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
    },
    resultRow: {
        display: 'flex', alignItems: 'center', gap: 8,
        padding: 10, background: '#f8fafc', border: '1px solid #e2e8f0',
        borderRadius: 6, marginBottom: 6,
    },
    modalAssignBtn: {
        padding: '7px 12px', background: '#10b981', color: '#fff',
        border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
    },
    modalCancelBtn: {
        padding: '6px 12px', background: '#fff', color: '#64748b',
        border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
    },
};

export default AdminClinicClaimManager;
